import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Rating,
  Select,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  adminApi,
  PLATFORM_FEEDBACK_STATUSES,
  PLATFORM_FEEDBACK_TYPES,
  PlatformFeedbackReport,
  PlatformFeedbackRow,
  PlatformFeedbackStatus,
  TrialFeedbackReport,
  TrialFeedbackRow,
} from '../services/api';
import PageHelp from '../components/PageHelp';
import { PAGE_HELP } from '../components/page-help-content';
import {
  ALL,
  EMPTY_FILTERS,
  PlatformFeedbackFilters,
  filterPlatformFeedback,
  statusChipColor,
  statusLabel,
  submitterLabel,
  typeLabel,
} from './feedback/platform-feedback-filters';

/**
 * Feedback page — ONE place for everything users tell us (Trello #371).
 *
 * Two tabs, because they are genuinely different objects:
 *  - "Ideas & Feedback": in-app submissions (suggestion / tool idea / bug) that
 *    the team TRIAGES through a status pipeline. Default tab — it's the one
 *    with outstanding work attached.
 *  - "Trial Reviews": the one-shot end-of-trial star rating + written answers
 *    (Trello #204). Read-only, unchanged.
 *
 * Both are stack-scoped server-side via the admin's JWT tenant.
 */

// ---------------------------------------------------------------------------
// Ideas & Feedback (Trello #371)
// ---------------------------------------------------------------------------

const PlatformFeedbackPanel: React.FC = () => {
  const [report, setReport] = useState<PlatformFeedbackReport | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<PlatformFeedbackFilters>(EMPTY_FILTERS);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    adminApi
      .getPlatformFeedback()
      .then((response) => {
        if (response.success) {
          setReport(response.data);
        } else {
          setError('Failed to load feedback.');
        }
      })
      .catch((err: any) => {
        setError(err?.response?.data?.message || err?.message || 'Failed to load feedback.');
      })
      .finally(() => setLoading(false));
  }, []);

  // No "already ran" ref alongside a cancelled flag — under StrictMode the
  // second mount would early-return and the first fetch's setState would be
  // suppressed, leaving the table permanently empty (see
  // general/strictmode-fetch-once-no-ref-guard.md).
  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => report?.items ?? [], [report]);
  const visibleRows = useMemo(() => filterPlatformFeedback(rows, filters), [rows, filters]);
  const summary = report?.summary;

  const handleStatusChange = async (row: PlatformFeedbackRow, status: PlatformFeedbackStatus) => {
    setSavingId(row.id);
    try {
      const response = await adminApi.updatePlatformFeedbackStatus(row.id, status);
      if (!response.success) throw new Error('Update failed');
      // Patch in place rather than refetching: the admin is mid-triage and a
      // refetch would re-sort the table under their cursor.
      setReport((prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.map((item) =>
                item.id === row.id ? { ...item, ...response.data } : item,
              ),
            }
          : prev,
      );
      setToast(`Moved to "${statusLabel(status)}"`);
    } catch (err: any) {
      setToast(err?.response?.data?.message || err?.message || 'Could not update the status.');
    } finally {
      setSavingId(null);
    }
  };

  const cards: { key: string; caption: string; value: string }[] = [
    { key: 'total', caption: 'Submissions', value: (summary?.total ?? 0).toLocaleString() },
    { key: 'open', caption: 'Awaiting triage', value: (summary?.open ?? 0).toLocaleString() },
    {
      key: 'completed',
      caption: 'Completed',
      value: (summary?.byStatus?.completed ?? 0).toLocaleString(),
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ mb: 3 }}
        data-testid="ideas-summary-cards"
      >
        {cards.map((card) => (
          <Card key={card.key} sx={{ flex: 1 }} data-testid={`ideas-card-${card.key}`}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                {card.caption}
              </Typography>
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, mt: 1 }}
                data-testid={`ideas-card-${card.key}-value`}
              >
                {card.value}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="ideas-type-filter-label">Type</InputLabel>
          <Select
            labelId="ideas-type-filter-label"
            label="Type"
            value={filters.type}
            data-testid="ideas-type-filter"
            onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value as typeof f.type }))}
          >
            <MenuItem value={ALL}>All types</MenuItem>
            {PLATFORM_FEEDBACK_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {typeLabel(type)}
                {summary?.byType?.[type] ? ` (${summary.byType[type]})` : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 190 }}>
          <InputLabel id="ideas-status-filter-label">Status</InputLabel>
          <Select
            labelId="ideas-status-filter-label"
            label="Status"
            value={filters.status}
            data-testid="ideas-status-filter"
            onChange={(e) =>
              setFilters((f) => ({ ...f, status: e.target.value as typeof f.status }))
            }
          >
            <MenuItem value={ALL}>All statuses</MenuItem>
            {PLATFORM_FEEDBACK_STATUSES.map((status) => (
              <MenuItem key={status} value={status}>
                {statusLabel(status)}
                {summary?.byStatus?.[status] ? ` (${summary.byStatus[status]})` : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          label="Search"
          placeholder="Title, detail or submitter"
          value={filters.search}
          data-testid="ideas-search"
          onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
          sx={{ minWidth: 240 }}
        />
      </Stack>

      <Paper variant="outlined">
        <TableContainer>
          <Table size="small" data-testid="ideas-table">
            <TableHead sx={{ bgcolor: 'action.hover' }}>
              <TableRow>
                <TableCell sx={{ width: 48 }} />
                <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Submitted by</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {rows.length === 0
                        ? 'No feedback or ideas submitted yet.'
                        : 'No submissions match these filters.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {visibleRows.map((row) => {
                const expanded = expandedId === row.id;
                return (
                  <React.Fragment key={row.id}>
                    <TableRow hover data-testid={`ideas-row-${row.id}`}>
                      <TableCell>
                        <IconButton
                          size="small"
                          aria-label={expanded ? 'Hide detail' : 'Show detail'}
                          onClick={() => setExpandedId(expanded ? null : row.id)}
                        >
                          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>{row.title}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Chip size="small" variant="outlined" label={typeLabel(row.type)} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{submitterLabel(row)}</Typography>
                        {row.organizationId && (
                          <Typography variant="caption" color="text.secondary">
                            {row.organizationId}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title={new Date(row.createdAt).toLocaleString()}>
                          <Typography variant="body2">
                            {new Date(row.createdAt).toLocaleDateString()}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Select
                          size="small"
                          value={row.status}
                          disabled={savingId === row.id}
                          data-testid={`ideas-status-${row.id}`}
                          onChange={(e) =>
                            handleStatusChange(row, e.target.value as PlatformFeedbackStatus)
                          }
                          renderValue={(value) => (
                            <Chip
                              size="small"
                              color={statusChipColor(value as PlatformFeedbackStatus)}
                              label={statusLabel(value as PlatformFeedbackStatus)}
                            />
                          )}
                          sx={{ minWidth: 170 }}
                        >
                          {PLATFORM_FEEDBACK_STATUSES.map((status) => (
                            <MenuItem key={status} value={status}>
                              {statusLabel(status)}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ py: 0, borderBottom: expanded ? undefined : 'none' }} colSpan={6}>
                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2 }} data-testid={`ideas-detail-${row.id}`}>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {row.description}
                            </Typography>
                            {row.statusUpdatedAt && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mt: 1.5 }}
                              >
                                Status last changed {new Date(row.statusUpdatedAt).toLocaleString()}
                              </Typography>
                            )}
                            {row.adminNote && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block' }}
                              >
                                Note: {row.adminNote}
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {report?.truncated && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Showing the most recent {report.limit} submissions.
        </Typography>
      )}

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast ?? ''}
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// Trial reviews (Trello #204) — unchanged, just moved behind a tab
// ---------------------------------------------------------------------------

const TrialReviewsPanel: React.FC = () => {
  const [report, setReport] = useState<TrialFeedbackReport | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminApi
      .getTrialFeedback()
      .then((response) => {
        if (cancelled) return;
        if (response.success) {
          setReport(response.data);
        } else {
          setError('Failed to load feedback.');
        }
      })
      .catch((err: any) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || err?.message || 'Failed to load feedback.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const summary = report?.summary;
  const rows = report?.items ?? [];

  const cards: { key: string; caption: string; value: string }[] = [
    { key: 'total', caption: 'Reviews', value: (summary?.total ?? 0).toLocaleString() },
    {
      key: 'avg',
      caption: 'Average rating',
      value: summary?.averageRating != null ? `${summary.averageRating} / 5` : '—',
    },
    {
      key: 'comments',
      caption: 'With written feedback',
      value: (summary?.withComments ?? 0).toLocaleString(),
    },
  ];

  return (
    <>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && report && (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }} data-testid="summary-cards">
            {cards.map((card) => (
              <Card key={card.key} sx={{ flex: 1 }} data-testid={`card-${card.key}`}>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">
                    {card.caption}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }} data-testid={`card-${card.key}-value`}>
                    {card.value}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Stack>

          <Paper variant="outlined">
            <TableContainer>
              <Table size="small" data-testid="feedback-table">
                <TableHead sx={{ bgcolor: 'action.hover' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Rating</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Most useful</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Suggestions / improvements</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No feedback yet on this stack.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {rows.map((row: TrialFeedbackRow) => (
                    <TableRow key={row.id} hover data-testid={`feedback-row-${row.id}`}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {typeof row.rating === 'number' ? (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Rating value={row.rating} readOnly size="small" />
                            <Typography variant="caption" color="text.secondary">
                              {row.rating}/5
                            </Typography>
                          </Stack>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 280, whiteSpace: 'pre-wrap' }}>{row.mostUseful || '—'}</TableCell>
                      <TableCell sx={{ maxWidth: 280, whiteSpace: 'pre-wrap' }}>{row.suggestions || '—'}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.email || row.userId || '—'}</Typography>
                        {row.organizationId && (
                          <Typography variant="caption" color="text.secondary">
                            {row.organizationId}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title={new Date(row.createdAt).toLocaleString()}>
                          <Typography variant="body2">{new Date(row.createdAt).toLocaleDateString()}</Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {report.truncated && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Showing the most recent {report.limit} reviews.
            </Typography>
          )}
        </>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------

const FeedbackPage: React.FC = () => {
  const [tab, setTab] = useState<'ideas' | 'trial'>('ideas');

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Feedback
        </Typography>
        <Typography variant="body2" color="text.secondary">
          What users tell us — in-app ideas and bug reports to triage, plus the end-of-trial
          reviews.
        </Typography>
      </Box>

      <PageHelp {...PAGE_HELP.feedback} />

      <Tabs
        value={tab}
        onChange={(_e, value) => setTab(value)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Ideas & Feedback" value="ideas" data-testid="tab-ideas" />
        <Tab label="Trial Reviews" value="trial" data-testid="tab-trial" />
      </Tabs>

      {/* Mounted lazily per tab so each panel fetches only when opened — the
          trial-reviews read is a separate stack-scoped query and there's no
          reason to pay for it while triaging ideas. */}
      {tab === 'ideas' ? <PlatformFeedbackPanel /> : <TrialReviewsPanel />}
    </Box>
  );
};

export default FeedbackPage;
